import { useMemo } from 'react';

import { AgentProfileResponseDto } from '@shared/services/apiService';
import { AgentProfileTypeEnum } from '@pages/Admin/Assistants/ui/types';

import { DEFAULT_NO_ROLE_AGENT_PROFILE } from '../constants';

export type ProfileGroupKey = 'system' | 'custom' | 'customer-template';

export interface ProfileGroup {
  key: ProfileGroupKey;
  labelKey: string;
  items: AgentProfileResponseDto[];
}

export interface FilteredProfiles {
  pinnedNoRole: AgentProfileResponseDto | null;
  groups: ProfileGroup[];
  totalCount: number;
  hasResults: boolean;
}

const GROUP_ORDER: { key: ProfileGroupKey; labelKey: string }[] = [
  { key: AgentProfileTypeEnum.SYSTEM, labelKey: 'campaignPage.profilePicker.groupSystem' },
  { key: AgentProfileTypeEnum.CUSTOM, labelKey: 'campaignPage.profilePicker.groupCustom' },
  {
    key: AgentProfileTypeEnum['CUSTOMER-TEMPLATE'],
    labelKey: 'campaignPage.profilePicker.groupTemplates',
  },
];

const normalize = (value: string | undefined | null): string => (value ?? '').toLowerCase().trim();

// Lower score = better rank (exact > prefix > contains).
const rank = (haystack: string, needle: string): number => {
  if (!needle) return 0;
  const h = normalize(haystack);
  if (!h) return Number.POSITIVE_INFINITY;
  if (h === needle) return 0;
  if (h.startsWith(needle)) return 1;
  if (h.includes(needle)) return 2;
  return Number.POSITIVE_INFINITY;
};

const matches = (profile: AgentProfileResponseDto, query: string): number => {
  if (!query) return 0;
  const nameRank = rank(profile.name, query);
  if (nameRank !== Number.POSITIVE_INFINITY) return nameRank;
  // Fall back to prompt content match (lower priority bucket).
  const promptRank = rank(profile.prompt ?? '', query);
  if (promptRank !== Number.POSITIVE_INFINITY) return 10 + promptRank;
  return Number.POSITIVE_INFINITY;
};

export const useFilteredProfiles = (
  profiles: AgentProfileResponseDto[] | undefined,
  rawQuery: string,
): FilteredProfiles => {
  return useMemo(() => {
    const query = normalize(rawQuery);
    const safeList = profiles ?? [];

    // Bucket by type.
    const buckets = new Map<
      ProfileGroupKey,
      { profile: AgentProfileResponseDto; score: number }[]
    >();
    for (const def of GROUP_ORDER) buckets.set(def.key, []);

    for (const profile of safeList) {
      // The synthetic "No Profile" placeholder is always pinned; never duplicate it inside a group.
      if (profile.id === DEFAULT_NO_ROLE_AGENT_PROFILE.id) continue;
      const type = (profile.type as ProfileGroupKey) ?? AgentProfileTypeEnum.CUSTOM;
      const bucket = buckets.get(type);
      if (!bucket) continue;
      const score = matches(profile, query);
      if (score === Number.POSITIVE_INFINITY) continue;
      bucket.push({ profile, score });
    }

    const groups: ProfileGroup[] = GROUP_ORDER.map(({ key, labelKey }) => {
      const bucket = buckets.get(key) ?? [];
      bucket.sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        const an = normalize(a.profile.name);
        const bn = normalize(b.profile.name);
        return an.localeCompare(bn);
      });
      return { key, labelKey, items: bucket.map((b) => b.profile) };
    }).filter((group) => group.items.length > 0);

    // Pinned No Profile: always available when the query is empty; otherwise only if it
    // matches the query so users can still filter it out alongside other rows.
    const noRoleScore = matches(DEFAULT_NO_ROLE_AGENT_PROFILE, query);
    const pinnedNoRole =
      query.length === 0 || noRoleScore !== Number.POSITIVE_INFINITY
        ? DEFAULT_NO_ROLE_AGENT_PROFILE
        : null;

    const totalCount =
      groups.reduce((acc, group) => acc + group.items.length, 0) + (pinnedNoRole ? 1 : 0);

    return {
      pinnedNoRole,
      groups,
      totalCount,
      hasResults: totalCount > 0,
    };
  }, [profiles, rawQuery]);
};
